/**
 * useMarkDataPlate — "This is a data plate" (callable fieldService_galleryMarkDataPlate; ISS-612).
 *
 * NOT optimistic, on purpose: "This plate is for:" is offered the moment the photo counts as a plate, and its server call
 * refuses a photo that is not yet a plate. So the photo is patched to a plate only once the server has said yes (the button
 * shows "Saving..." until then), and everything is revalidated on settle. The server decides everything that matters (who
 * may, a label a person already chose, a removed photo); its plain-language refusal is shown as the toast.
 */
import { useMutation } from '@tanstack/react-query';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import toast from 'react-hot-toast';
import { functions } from '../../config/firebase';
import { useOrganization } from '../../contexts/OrganizationContext';
import { galleryErrorMessage, useGalleryCache } from './useGeneratorGallery';

export interface MarkDataPlateResult {
  photoId: string;
  /** false = it already was a plate: nothing was written. */
  changed: boolean;
}

const markDataPlateFn = httpsCallable<{ organizationId: string; photoId: string }, MarkDataPlateResult>(
  functions,
  'fieldService_galleryMarkDataPlate'
);

export function useMarkDataPlate(generatorDocId: string) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  const cache = useGalleryCache(orgId, generatorDocId);

  return useMutation({
    mutationFn: async ({ photoId }: { photoId: string }) => (await markDataPlateFn({ organizationId: orgId, photoId })).data,
    onError: (err) => {
      toast.error(galleryErrorMessage(err, 'Could not tag this photo as a data plate.'));
    },
    onSuccess: (result, { photoId }) => {
      cache.queryClient.setQueryData<Array<{ id: string; category?: string | null }>>(
        cache.galleryKey,
        (old) => old?.map((r) => (r.id === photoId ? { ...r, category: 'data-plate' } : r))
      );
      cache.patchLibraries((row) => (row.id === photoId ? { ...row, category: 'data-plate' } : row));
      toast.success(result.changed ? 'Tagged as a data plate' : 'Already a data plate');
    },
    onSettled: () => {
      cache.invalidateAll();
    },
  });
}
